import React, { useCallback, useEffect, useState } from 'react';
import styles from './create-user-form.module.scss';
import {
  Alert,
  DefaultButton,
  FormButtonBack,
  FormInput,
  MultiSelectInput,
} from 'shared/ui';
import { MailConfirmationInput } from 'features/index';
import { useNavigate } from 'react-router-dom';
import { BusinessApi } from 'shared/api';
import { IPointListDto } from 'shared/api/business/interfaces/point-list-dto.interface';
import { VerificationCodeEnum } from 'shared/types';
import { useAppMediaQuery } from 'shared/hooks';

interface CreateUserFormProps {
  title: string;
  buttonText: string;
  fioLabel: string;
  businessId: string;
  pointId?: string;
  navigateLink: string;
  pointFilter?: { adminFree?: boolean };
  extraFields?: React.ReactNode;
  hideStandardFields?: boolean;
  successMessage?: string;
  onSubmit: (data: {
    fio: string;
    email: string;
    phone: string;
    password: string;
    confirmPassword: string;
    pointIds: string[];
  }) => Promise<void>;
  validate?: (data: {
    fio: string;
    email: string;
    phone: string;
    password: string;
    confirmPassword: string;
    pointIds: string[];
  }) => string | false | undefined;
}

export const CreateUserForm: React.FC<CreateUserFormProps> = ({
  title,
  buttonText,
  fioLabel,
  businessId,
  navigateLink,
  pointFilter,
  extraFields,
  hideStandardFields,
  successMessage,
  onSubmit,
  validate,
}) => {
  const isTablet = useAppMediaQuery('tablet');
  const [fio, setFio] = useState('');
  const [email, setMail] = useState('');
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [points, setPoints] = useState<IPointListDto[]>([]);
  const [selectedPoints, setSelectedPoints] = useState<IPointListDto[]>([]);
  const [validationError, setValidationError] = useState('');
  const [alertMessage, setAlertMessage] = useState<string | null>(null);
  const handleCloseAlert = useCallback(() => setAlertMessage(null), []);
  const navigate = useNavigate();

  const fetchPoints = async () => {
    try {
      if (businessId) {
        const pointsInfo = await BusinessApi.getPointList({
          businessId,
          ...pointFilter,
        });
        if (pointsInfo) {
          setPoints(pointsInfo);
        }
      }
    } catch {
      setValidationError('Ошибка при получении данных о точках');
    }
  };

  useEffect(() => {
    fetchPoints();
  }, [businessId]);

  const handleSubmit = async () => {
    const pointIds = selectedPoints.map((point) => point.id);

    if (validate) {
      const errors = validate({
        fio,
        email,
        phone,
        password,
        confirmPassword,
        pointIds,
      });
      if (errors) {
        setValidationError(errors);
        return;
      }
    }

    try {
      await onSubmit({
        fio,
        email,
        phone,
        password,
        confirmPassword,
        pointIds,
      });
      if (successMessage) {
        setAlertMessage(successMessage);
        setTimeout(() => navigate(navigateLink), 1500);
      } else {
        navigate(navigateLink);
      }
    } catch (error) {
      if (error instanceof Error) {
        setValidationError(error.message);
      }
    }
  };

  return (
    <div className={styles.create_user}>
      <div className={styles.create_user__content}>
        <div className={styles.create_user__title}>{title}</div>
        <div className={styles.create_user__inputs}>
          {extraFields}
          <div className={styles.inputs__input}>
            <MultiSelectInput<IPointListDto>
              options={points}
              inputTitle="С какими точками работает"
              selectedOptions={selectedPoints}
              onChange={setSelectedPoints}
              displayField="name"
              getOptionId={(point) => point.id}
              placeholder="Выберите точки"
            />
          </div>
          {!hideStandardFields && (
            <>
              <div className={styles.inputs__input}>
                <FormInput
                  inputTitle={fioLabel}
                  inputPlaceholder="ФИО"
                  onChange={(value) => setFio(value)}
                />
              </div>
              <div className={styles.inputs__input}>
                <MailConfirmationInput
                  onChange={(value) => setMail(value)}
                  type={VerificationCodeEnum.EMAIL_VERIFICATION}
                />
              </div>
              <div className={styles.inputs__input}>
                <FormInput
                  isPhone={true}
                  inputTitle="Номер телефона"
                  onChange={(value) =>
                    setPhone(value.match(/\d+/g)?.join('') ?? '')
                  }
                />
              </div>
              <div className={styles.inputs__input}>
                <FormInput
                  inputType="password"
                  showPasswordToggle
                  inputTitle="Пароль"
                  inputPlaceholder="Пароль"
                  onChange={(value) => setPassword(value)}
                />
              </div>
              <div className={styles.inputs__input}>
                <FormInput
                  inputType="password"
                  showPasswordToggle
                  inputTitle="Повторите пароль"
                  inputPlaceholder="Пароль"
                  onChange={(value) => setConfirmPassword(value)}
                />
              </div>
            </>
          )}
        </div>
        <div className={styles.create_user__buttons}>
          <div className={styles.buttons__error}>{validationError}</div>
          <div className={styles.buttons__button_submit} onClick={handleSubmit}>
            <DefaultButton
              buttonText={buttonText}
              color="white"
              style={
                isTablet ? { fontSize: '18px', padding: '0px 12px' } : undefined
              }
            />
          </div>
          <div className={styles.buttons__button_back}>
            <FormButtonBack />
          </div>
        </div>
      </div>
      {alertMessage && (
        <Alert
          type="success"
          message={alertMessage}
          onClose={handleCloseAlert}
        />
      )}
    </div>
  );
};
